import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Skeleton } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { WalletModelShort } from '@root/entities/wallet/ui'
import { useTranslation } from '@root/shared/localization'
import { CopyButton, Input } from '@root/shared/ui-kit'

import type { TransferFlowReatom } from '../../../atoms'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  model: TransferFlowReatom
} & ClassNameProps

export const TransferReviewHeader: React.FC<Props> = ({ className, model }) => {
  const { t } = useTranslation()

  const wallet = model.wallet
  const [address] = useAtom(model.destination.addressAtom)
  const [destinationWallet] = useAtom(model.destination.destinationWalletAtom)
  const [currency] = useAtom(model.currency.currencyAtom)
  const [amount] = useAtom(model.amount.amountAtom)
  const [amountUsd] = useAtom(model.amount.amountUsdStateAtom)

  return (
    <Root className={className}>
      <TransferPath>
        <WalletContainer>
          <WalletModelShort wallet={wallet} />
        </WalletContainer>
        <SendTo>{t('transfer.send-to')}</SendTo>
        {destinationWallet ? (
          <WalletContainer>
            <WalletModelShort wallet={destinationWallet} />
          </WalletContainer>
        ) : (
          <Input
            readOnly
            value={address}
            endContent={<CopyButton value={address} />}
          />
        )}
      </TransferPath>
      <TransferAmount>
        <CurrencyContainer>
          <CurrencyIcon currency={currency} size={24} />
          <span>{currency.title}</span>
        </CurrencyContainer>
        <Amount>
          <Numeric value={amount} preset="currency-amount-short" />
          <Ticker> {currency.ticker}</Ticker>
        </Amount>
        <AmountUsd>
          {amountUsd.value ? (
            <Numeric
              value={amountUsd.value}
              preset="currency-amount-short"
              currency="$"
            />
          ) : (
            <Skeleton height={12} width={40} paddingBlock={1} />
          )}
        </AmountUsd>
      </TransferAmount>
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TransferPath = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const TransferAmount = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const WalletContainer = styled.div`
  display: grid;
  justify-content: center;
`

const SendTo = styled.div`
  color: var(--text-9);
  text-align: center;
  font-size: 12px;
  font-weight: 400;
`

const CurrencyContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  justify-content: center;
  gap: 7px;

  color: var(--text-12);
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
`

const Amount = styled.div`
  color: var(--text-12);
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  line-height: 24px;
`

const AmountUsd = styled.div`
  color: var(--text-9);
  text-align: center;
  font-size: 10px;
  font-weight: 600;
  line-height: 12px;

  display: grid;
  justify-content: center;
`

const Ticker = styled.span`
  font-size: 16px;
  line-height: 16px;
`
